import React from 'react';
import { motion } from 'motion/react';
import { ExternalLink, Activity, Brain, CheckCircle, Sparkles, Heart } from 'lucide-react';

const Services = () => {
  // Update page title
  React.useEffect(() => {
    document.title = "Our Solutions - Kodamalas Krushi Foundation | BoneAH, IQplayground & Speak to the God";
  }, []);
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const boneAHFeatures = [
    "AI-powered bone age assessment",
    "Early detection of growth disorders",
    "Non-invasive diagnostic approach",
    "Accurate and rapid results",
    "Accessible healthcare solution"
  ];

  const iqPlaygroundFeatures = [
    "Interactive cognitive assessments",
    "Engaging learning activities",
    "Mental development tracking",
    "Age-appropriate challenges",
    "Progress monitoring tools"
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="display-hero text-[var(--text-primary)] mb-6"
          >
            Our Technology Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="body-large text-[var(--text-secondary)]"
          >
            Comprehensive AI-powered solutions for physical health, mental development, and emotional wellbeing
          </motion.p>
        </div>
      </section>

      {/* BoneAH Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-[var(--brand-primary)] rounded-2xl flex items-center justify-center">
                  <Activity size={32} className="text-white" />
                </div>
                <h2 className="heading-large text-[var(--text-primary)]">BoneAH</h2>
              </div>
              
              <p className="body-large text-[var(--text-secondary)] mb-6">
                BoneAH is our cutting-edge AI-based tool that revolutionizes how we identify physical growth issues in children. Using advanced machine learning algorithms, BoneAH analyzes bone development patterns to detect potential growth disorders early, enabling timely intervention and better health outcomes.
              </p>

              <div className="space-y-3 mb-8">
                {boneAHFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={20} className="text-[var(--brand-primary)] flex-shrink-0" />
                    <span className="body-standard text-[var(--text-secondary)]">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <a 
                href="https://boneah.com/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <button className="btn-primary flex items-center gap-2 hover-scale">
                  Visit BoneAH
                  <ExternalLink size={18} />
                </button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-3xl overflow-hidden border border-[var(--border-light)]"
            >
              <div className="relative h-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_child-growth-hub-1/artifacts/5v8nuxru_3d-cartoon-hospital-healthcare-scene.jpg" 
                  alt="Doctor examining child in healthcare setting"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
              </div>
              <div className="p-10 lg:p-12">
              <div className="space-y-6">
                <div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3">Physical Growth Monitoring</h3>
                  <p className="body-standard text-[var(--text-secondary)]">
                    Early detection of growth-related health issues is crucial for effective treatment. BoneAH makes advanced diagnostic capabilities accessible, ensuring every child has the opportunity for healthy development.
                  </p>
                </div>
                <div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3">AI-Powered Precision</h3>
                  <p className="body-standard text-[var(--text-secondary)]">
                    Our sophisticated artificial intelligence models analyze bone development with remarkable accuracy, providing healthcare professionals with reliable insights for informed decision-making.
                  </p>
                </div>
                <div className="glass-card rounded-2xl p-6 border-l-4 border-[var(--brand-primary)]">
                  <p className="body-standard text-[var(--text-primary)] font-medium italic">
                    "BoneAH represents a significant leap forward in pediatric healthcare, making advanced diagnostics accessible to communities that need it most."
                  </p>
                </div>
              </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* IQplayground Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20 bg-[var(--bg-secondary)] py-20">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-purple)] to-[#8B5CF6] rounded-2xl flex items-center justify-center shadow-lg shadow-[var(--brand-purple)]/50">
                  <Brain size={32} className="text-white" />
                </div>
                <h2 className="heading-large text-[var(--text-primary)]">IQplayground</h2>
              </div>
              
              <p className="body-large text-[var(--text-secondary)] mb-6">
                IQplayground is an innovative application designed to assess and enhance mental growth in children. Through interactive activities and gamified learning experiences, we help identify cognitive development patterns and provide tailored support for each child's unique learning journey.
              </p>

              <div className="space-y-3 mb-8">
                {iqPlaygroundFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springConfig, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={20} className="text-[var(--brand-primary)] flex-shrink-0" />
                    <span className="body-standard text-[var(--text-secondary)]">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <button className="btn-primary flex items-center gap-2 hover-scale">
                Learn More
                <Sparkles size={18} />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-3xl overflow-hidden order-1 lg:order-2 border border-[var(--border-light)]"
            >
              <div className="relative h-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_child-growth-hub-1/artifacts/dl9etdit_3d-cartoon-hospital-healthcare-scene%20%281%29.jpg" 
                  alt="Doctor and child in cognitive assessment setting"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
              </div>
              <div className="p-10 lg:p-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="heading-medium text-[var(--text-primary)] mb-3">Cognitive Development</h3>
                    <p className="body-standard text-[var(--text-secondary)]">
                      Mental growth is just as important as physical development. IQplayground provides a comprehensive platform for assessing and enhancing cognitive abilities through engaging, research-backed activities.
                    </p>
                  </div>
                  <div>
                    <h3 className="heading-medium text-[var(--text-primary)] mb-3">Engaging & Educational</h3>
                    <p className="body-standard text-[var(--text-secondary)]">
                      Our application combines entertainment with education, creating an environment where children naturally develop critical thinking, problem-solving, and analytical skills.
                    </p>
                  </div>
                  <div className="glass-card rounded-2xl p-6 border-l-4 border-[var(--brand-purple)]">
                    <p className="body-standard text-[var(--text-primary)] font-medium italic">
                      "IQplayground transforms mental development assessment into an enjoyable journey of discovery and growth for every child."
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Speak to the God Section */}
      <section className="px-6 md:px-8 lg:px-12 mb-20">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-[#FF6B6B] rounded-2xl flex items-center justify-center">
                  <Heart size={32} className="text-white" />
                </div>
                <h2 className="heading-large text-[var(--text-primary)]">Speak to the God</h2>
              </div>
              
              <p className="body-large text-[var(--text-secondary)] mb-6">
                Our upcoming application focuses on emotional wellbeing and spiritual support for children. Speak to the God provides a safe space for children to express their feelings, seek guidance, and develop emotional intelligence through compassionate AI-powered conversations.
              </p>

              <div className="space-y-3 mb-8">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={springConfig}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={20} className="text-[#FF6B6B] flex-shrink-0" />
                  <span className="body-standard text-[var(--text-secondary)]">Emotional support and guidance</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={20} className="text-[#FF6B6B] flex-shrink-0" />
                  <span className="body-standard text-[var(--text-secondary)]">Safe space for expression</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: 0.2 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={20} className="text-[#FF6B6B] flex-shrink-0" />
                  <span className="body-standard text-[var(--text-secondary)]">Spiritual and emotional balance</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: 0.3 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle size={20} className="text-[#FF6B6B] flex-shrink-0" />
                  <span className="body-standard text-[var(--text-secondary)]">Building resilience and inner peace</span>
                </motion.div>
              </div>

              <button className="btn-primary flex items-center gap-2 hover-scale opacity-50 cursor-not-allowed" style={{background: '#FF6B6B'}}>
                Coming Soon
                <Sparkles size={18} />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-3xl overflow-hidden border border-[var(--border-light)]"
            >
              <div className="relative h-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_child-growth-hub-1/artifacts/a9oru3zs_child-with-autism-living-fantasy-world.jpg" 
                  alt="Child exploring emotions in imaginative world"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
              </div>
              <div className="p-10 lg:p-12">
              <div className="space-y-6">
                <div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3">Emotional Intelligence</h3>
                  <p className="body-standard text-[var(--text-secondary)]">
                    Emotional wellbeing is as crucial as physical and mental health. Speak to the God helps children understand and manage their emotions, building a foundation for lifelong emotional resilience.
                  </p>
                </div>
                <div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3">Spiritual Guidance</h3>
                  <p className="body-standard text-[var(--text-secondary)]">
                    Through compassionate AI conversations, children can explore their feelings, find comfort in difficult times, and develop a sense of inner peace and purpose.
                  </p>
                </div>
                <div className="glass-card rounded-2xl p-6 border-l-4 border-[var(--brand-coral)]">
                  <p className="body-standard text-[var(--text-primary)] font-medium italic">
                    "Nurturing emotional wellbeing creates resilient, confident children ready to face life's challenges with grace and strength."
                  </p>
                </div>
              </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="px-6 md:px-8 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-dark)] rounded-3xl p-12 text-center text-white"
          >
            <h2 className="heading-large mb-4">Complete Child Development Support</h2>
            <p className="body-large mb-8 max-w-3xl mx-auto">
              By integrating physical health monitoring (BoneAH), mental development (IQplayground), and emotional wellbeing (Speak to the God), we provide comprehensive support for every aspect of a child's growth.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div>
                <div className="text-5xl font-bold mb-2">AI</div>
                <p className="body-standard">Powered Solutions</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">3</div>
                <p className="body-standard">Core Pillars</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">100+</div>
                <p className="body-standard">Lives Impacted</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;